import { useCallback, useMemo, useState } from 'react'

import { useReconciliationEntitled } from '@/modules/account'
import { getProjectPath } from '@/utils'
import { askAboutCase } from '../askAboutCase'
import { ActiveTourStep } from './ActiveTourStep'
import { tourStepsFor } from './steps'
import { useFollowStepPage } from './useFollowStepPage'

export * from './useTrialTour'

type Direction = 1 | -1

interface TourPosition {
  index: number
  direction: Direction
}

interface TrialTourProps {
  project: { slug: string; isAiFeaturesEnabled?: boolean | null }
  onEnd: () => void
}

// Walks a trial user through the places that matter on their results. A step whose area never
// shows up is skipped in the direction the user was going.
export const TrialTour = ({ project, onEnd }: TrialTourProps) => {
  const isChatEnabled = Boolean(project.isAiFeaturesEnabled)
  const isReconciliationEntitled = useReconciliationEntitled()
  const steps = useMemo(
    () => tourStepsFor({ isChatEnabled, isReconciliationEntitled }),
    [isChatEnabled, isReconciliationEntitled],
  )
  const [position, setPosition] = useState<TourPosition>({
    index: 0,
    direction: 1,
  })
  const step = steps[position.index]
  const projectPath = getProjectPath(project)
  useFollowStepPage(projectPath, step.page, onEnd)

  const move = useCallback(
    (direction: Direction) => {
      const index = position.index + direction
      if (index >= steps.length) return onEnd()
      if (index < 0)
        return setPosition({ index: position.index + 1, direction: 1 })
      setPosition({ index, direction })
    },
    [position.index, steps.length, onEnd],
  )

  const skipMissing = useCallback(
    () => move(position.direction),
    [move, position.direction],
  )

  const askNow = () => {
    askAboutCase(project.slug)
    onEnd()
  }

  return (
    <ActiveTourStep
      key={position.index}
      step={step}
      number={position.index + 1}
      total={steps.length}
      onBack={() => move(-1)}
      onNext={() => move(1)}
      onSkip={onEnd}
      onAskNow={askNow}
      onMissing={skipMissing}
    />
  )
}
